/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { TicketAlertStrip } from '../../shared/ticket/TicketAlertStrip'
import { ticketChrome } from '../../shared/ticket/ticketChrome.styles';
import type { PromotionConflict } from '../application/promotionConflicts';

type Props = {
  conflicts: PromotionConflict[];
};

export function PromotionConflictBanner({ conflicts }: Props) {
  const [sheetOpen, setSheetOpen] = useState(false);
  if (conflicts.length === 0) return null;

  const singular = conflicts.length === 1;
  const title = `${conflicts.length} promo${singular ? '' : 's'} se pisa${singular ? '' : 'n'}`;
  const subtitle = 'Dos promos activas no se suman. Ver cuáles.';

  return (
    <>
      <TicketAlertStrip
        title={title}
        subtitle={subtitle}
        tone="warn"
        onPress={() => setSheetOpen(true)}
        testID="promotions.alert.conflicts"
        accessibilityLabel="Ver conflictos de promociones"
      />
      <ConflictDetailSheet
        visible={sheetOpen}
        conflicts={conflicts}
        onClose={() => setSheetOpen(false)}
      />
    </>
  );
}

function ConflictDetailSheet({
  visible,
  conflicts,
  onClose,
}: {
  visible: boolean;
  conflicts: PromotionConflict[];
  onClose: () => void;
}) {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;

  const sheetContent = (
    <>
      <View style={ticketChrome.sheetHeader}>
        <View>
          <Text style={ticketChrome.sheetTitle}>Conflictos</Text>
          <Text style={ticketChrome.sheetSubtitle}>
            {conflicts.length} promo{conflicts.length === 1 ? '' : 's'} se pisa{conflicts.length === 1 ? '' : 'n'}
          </Text>
        </View>
        <TouchableOpacity
          onPress={onClose}
          style={ticketChrome.sheetCloseBtn}
          activeOpacity={0.6}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        >
          <Text style={ticketChrome.sheetCloseGlyph}>×</Text>
        </TouchableOpacity>
      </View>
      <View style={ticketChrome.grabber} />
      <ScrollView style={styles.scrollBody} nestedScrollEnabled>
        {conflicts.slice(0, 30).map((c, idx) => (
          <View key={idx} style={styles.row}>
            <Text style={styles.rowText} numberOfLines={2}>
              <Text style={styles.promoName}>{c.promotionA.name}</Text>
              {' ↔ '}
              <Text style={styles.promoName}>{c.promotionB.name}</Text>
            </Text>
            <Text style={styles.reason}>{c.reason}</Text>
          </View>
        ))}
        {conflicts.length > 30 ? (
          <Text style={styles.more}>+{conflicts.length - 30} más…</Text>
        ) : null}
      </ScrollView>
    </>
  );

  if (isDesktop) {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <View style={[styles.backdrop, { justifyContent: 'center', alignItems: 'center' }]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <View style={[ticketChrome.sheetCard, { maxWidth: 480, borderRadius: 16 }]}>
            {sheetContent}
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View style={ticketChrome.sheetCard}>
          {sheetContent}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  scrollBody: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
  },
  row: {
    gap: 2,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  rowText: {
    fontSize: 14,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  promoName: {
    fontWeight: '700',
  },
  reason: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  more: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
    paddingTop: spacing.sm,
  },
});
